/**
 * K-T-12 — Layer-3 hook for canonical per-technician efficiency within
 * a chosen job category (drill-down sibling of K-T-10's
 * `useJobCategoryEfficiency`).
 *
 * Thin wrapper around `kpi_efficiency_by_job_category_tech(shop_id,
 * start_at, end_at, category)`. Returns one
 * `DrilldownTechEfficiencyResult` per technician with both display
 * totals and the canonical-filtered ratio + sample-gate fields.
 * Holds NO local KPI math — the RPC is the source of truth.
 *
 * Replaces the local `loadTechDrilldown` aggregation in
 * `JobCategoryPerformance.tsx`. The component continues to handle
 * sorting / formatting / display arrangement client-side.
 *
 * The query is gated by `category` — when `category` is null/undefined
 * (drill-down collapsed) the hook is disabled and returns `null` data.
 * When the user expands a different category, the query key changes
 * and react-query refetches. Re-expanding a previously seen category
 * is served from cache.
 *
 * Date-range conversion: shop-local `yyyy-MM-dd` strings → UTC ISO
 * timestamps via `fromZonedTime`, mirroring K-T-10 / K-T-11.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromZonedTime } from 'date-fns-tz';
import type { DrilldownTechEfficiencyResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseJobCategoryTechEfficiencyOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd`. */
  startDate: string;
  /** Shop-local `yyyy-MM-dd`. */
  endDate: string;
  shopTimezone: string;
  /** The expanded jobs.category. Hook is disabled when null/undefined. */
  category: string | null | undefined;
}

export interface UseJobCategoryTechEfficiencyResult {
  data: DrilldownTechEfficiencyResult[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Stable query key:
 *   ['kpi', 'kpi_efficiency_by_job_category_tech', shopId, startDate,
 *    endDate, shopTimezone, category]
 */
export function useJobCategoryTechEfficiency(
  opts: UseJobCategoryTechEfficiencyOptions,
): UseJobCategoryTechEfficiencyResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_efficiency_by_job_category_tech',
      opts.shopId,
      opts.startDate,
      opts.endDate,
      opts.shopTimezone,
      opts.category,
    ],
    queryFn: async (): Promise<DrilldownTechEfficiencyResult[] | null> => {
      const startAt = fromZonedTime(
        `${opts.startDate}T00:00:00`,
        opts.shopTimezone,
      ).toISOString();
      const endAt = fromZonedTime(
        `${opts.endDate}T23:59:59.999`,
        opts.shopTimezone,
      ).toISOString();

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: {
          p_shop_id: string;
          p_start_at: string;
          p_end_at: string;
          p_category: string;
        },
      ) => Promise<{
        data: DrilldownTechEfficiencyResult[] | null;
        error: unknown;
      }>)('kpi_efficiency_by_job_category_tech', {
        p_shop_id: opts.shopId,
        p_start_at: startAt,
        p_end_at: endAt,
        p_category: opts.category as string,
      });

      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(
      opts.shopId &&
        opts.startDate &&
        opts.endDate &&
        opts.shopTimezone &&
        opts.category,
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
